import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const REFRESH_TOKEN_ENDPOINT = '/api/refresh-token';


const api = axios.create({
  baseURL: apiUrl,
  timeout: 600000,
});


const isTokenExpired = (token) => {
  if (!token) return true;
  const expiry = JSON.parse(atob(token.split('.')[1])).exp;
  const now = Math.floor(new Date().getTime() / 1000);
  return now >= expiry;
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    isRefreshing = false;
    return Promise.reject('No refresh token available');
  }

  try {
    const response = await api.post(REFRESH_TOKEN_ENDPOINT, { refreshToken });
    const { token, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);
    isRefreshing = false;
    processQueue(null, token);
    return token;
  } catch (error) {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    isRefreshing = false;
    processQueue(error, null);
    throw error;
  }
};

// Request Interceptor
api.interceptors.request.use(async (config) => {
  // Skip token check for authenticate and refresh token requests
  if (config.url === REFRESH_TOKEN_ENDPOINT || config.url === '/api/authenticate') {
    return config;
  }

  let token = localStorage.getItem('token');
  let storedRefreshToken = localStorage.getItem('refreshToken');

  if (isTokenExpired(token)) {
    if (storedRefreshToken && !isTokenExpired(storedRefreshToken)) {
      try {
        token = await refreshToken();
      } catch (error) {
        console.error('Token refresh error:', error);
        // Clear tokens if refresh failed
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        token = null;
        storedRefreshToken = null;
      }
    } else {
      // Both tokens are missing or expired, attempt to login
      try {
        // You need to implement a way to securely store and retrieve username and password
        // This is just a placeholder - DO NOT store passwords in localStorage in a real app
        const username = localStorage.getItem('username');
        const password = localStorage.getItem('password');
        if (username && password) {
          await login(username, password, config);
          // login function will update the config with the new token
          return config;
        } else {
          throw new Error('No credentials available for automatic login');
        }
      } catch (loginError) {
        console.error('Automatic login failed:', loginError);
        throw new axios.Cancel('Authentication required. Please login.');
      }
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Response Interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    console.error('Response error:', error);

    // A rejection raised by the *request* interceptor itself (e.g. the
    // axios.Cancel thrown above when both tokens are gone and there's no
    // stored credential to auto-login with) never reaches the network, so it
    // carries no `config` - reading originalRequest.url below would throw a
    // TypeError that then masked the real "please log in again" error behind
    // a confusing "something went wrong" one.
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // A 401 here just means "wrong credentials" - not an expired session to
    // recover from. Letting it fall through to the refresh-then-cancel logic
    // below replaced the real error with a misleading "Session expired"
    // message on every failed login attempt.
    if (originalRequest.url === '/api/authenticate') {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const token = await refreshToken();
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Refresh token error:', refreshError);
        throw new axios.Cancel('Session expired. Please login again.');
      }
    }
    return Promise.reject(error);
  }
);

// Persists an already-issued {token, refreshToken, role} triple, same shape
// /api/authenticate returns. Used both by login() below and by the attendee
// signup/claim flow, which gets its own JWTs directly from the signup
// response body instead of a separate /api/authenticate call.
// Fired whenever the session changes (login, signup, logout) so UI that
// isn't on the stack that triggered the change - the navbar, most notably -
// can react immediately. A route change alone isn't a reliable signal: e.g.
// completing signup stores a session without ever navigating away from
// /signup, so nothing else would tell Header.js to re-read the role.
export const AUTH_CHANGED_EVENT = 'hearts-auth-changed';

// `role` is the account's primary role (used for "where's home for this
// account" type decisions, e.g. Header.js). `roles` is the full set this
// login actually has access to - an ATTENDEE account invited as a speaker
// has roles ['ATTENDEE','SPEAKER'], not just its primary role - and is what
// every access check (Protected*Route) should test against instead. Not
// every endpoint that creates a session sends `roles` yet, so this always
// derives a safe single-role fallback rather than leaving it unset.
export const storeSession = ({ token, refreshToken, role, roles }) => {
  if (token) localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  if (role) localStorage.setItem('role', role);
  if (roles) localStorage.setItem('roles', JSON.stringify(roles));
  else if (role) localStorage.setItem('roles', JSON.stringify([role]));
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
};

export const login = async (username, password, config = null) => {
  try {
    const response = await api.post('/api/authenticate', { username, password });

    if (response.data && response.data.token && response.data.refreshToken) {
      storeSession(response.data);

      // If this login was called from the interceptor, update the original request's header
      if (config) {
        config.headers.Authorization = `Bearer ${response.data.token}`;
      }

      return response.data;
    } else {
      console.error("Login response missing token or refreshToken:", response.data);
      throw new Error("Invalid response from server");
    }
  } catch (error) {
    console.error('Login error:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
      console.error('Error status:', error.response.status);
      console.error('Error headers:', error.response.headers);
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('role');
  localStorage.removeItem('roles');
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
};

export const getRole = () => localStorage.getItem('role');

// Falls back to a single-role array if 'roles' isn't set - covers a session
// stored before this existed (an already-logged-in browser, pre-deploy) as
// well as any endpoint that still only sends `role`.
export const getRoles = () => {
  try {
    const raw = localStorage.getItem('roles');
    if (raw) return JSON.parse(raw);
  } catch {
    // fall through to the single-role fallback below
  }
  const role = getRole();
  return role ? [role] : [];
};

// The one case where "register for event X" has to survive a login that
// isn't itself token-based: an anonymous visitor with an *existing* account
// clicks "Register", lands on /signup?eventId=X, is told to log in instead,
// and follows that link to /login?eventId=X (see AttendeeSignup's 'exists'
// step). Every other signup/verification path resolves the pending event
// server-side, keyed off the emailed token itself (see
// AttendeeAccountService.registerForPendingEvent) - it doesn't need this at
// all. So `eventId` is threaded through as a plain value (URL query param ->
// component prop -> this call) for the one login that actually needs it,
// instead of being stashed in sessionStorage as an ambient flag that any
// later, unrelated login in the same tab would also pick up.
export const registerForEvent = async (eventId) => {
  if (!eventId) return null;
  try {
    const { data } = await api.post(`/api/attendee/events/${eventId}/register`);
    return { eventId, success: true, eventName: data.eventName, eventNameEN: data.eventNameEN };
  } catch (error) {
    return { eventId, success: false, error: error.response?.data };
  }
};

// Turns a failed profile-save request into a message that actually tells the
// person what to do next, instead of the same "could not save, try again"
// string regardless of cause - a session-expired retry will never succeed,
// and a dropped connection reads very differently from a real server error.
// `t` is the caller's own react-intl formatter (id, defaultMessage) => string.
export const describeSaveError = (err, t) => {
  if (err instanceof axios.Cancel) {
    return t('profile.saveErrorSession', 'Your session has expired. Please log in again to save your changes.');
  }
  if (!err.response) {
    return t('profile.saveErrorNetwork', 'Could not reach the server. Please check your connection and try again.');
  }
  return t('profile.saveError', 'Could not save your profile. Please try again.');
};

export default api;
